<template>
    <Modal
        :isOpen="isOpen"
        @update:isOpen="(value) => (isOpen = value)"
        title="Filtered Snapshot Configuration"
        description="Configure filter conditions to capture only specific data from your selected topics"
        size="5xl"
        :showFooterButtons="true">
        <!-- Main Content -->
        <div class="flex flex-col space-y-4 min-h-0">
            <!-- Selected Topics List -->
            <div class="flex flex-col min-h-0">
                <div class="flex items-center justify-between mb-4 flex-shrink-0">
                    <div class="flex items-center gap-3">
                        <h3 class="text-lg font-semibold">Selected Topics</h3>
                        <Badge variant="secondary" class="text-sm">{{ selectedTopics.length }}</Badge>
                    </div>
                    <div class="flex gap-2">
                        <Button variant="ghost" size="sm" @click="clearAllFilters">
                            <RotateCcw class="h-4 w-4 mr-2" />
                            Clear All
                        </Button>
                        <Button variant="ghost" size="sm" @click="toggleAllExpanded">
                            {{ allExpanded ? 'Collapse All' : 'Expand All' }}
                        </Button>
                    </div>
                </div>

                <ScrollArea class="max-h-96 overflow-y-auto">
                    <div class="space-y-4 pr-2">
                        <div v-for="topic in selectedTopics" :key="topic.id || topic.name || topic">
                            <Card>
                                <Collapsible v-model:open="expandedTopics[getTopicId(topic)]">
                                    <CollapsibleTrigger as-child>
                                        <CardHeader
                                            class="cursor-pointer py-3"
                                            role="button"
                                            :aria-expanded="expandedTopics[getTopicId(topic)]"
                                            :aria-controls="`topic-content-${getTopicId(topic)}`"
                                            tabindex="0"
                                            @keydown.enter="toggleTopicExpanded(getTopicId(topic))"
                                            @keydown.space.prevent="toggleTopicExpanded(getTopicId(topic))">
                                            <div class="flex items-center justify-between w-full">
                                                <div class="flex items-center gap-4">
                                                    <ChevronRight
                                                        :class="[
                                                            'h-5 w-5 transition-transform duration-200',
                                                            expandedTopics[getTopicId(topic)] ? 'rotate-90' : ''
                                                        ]" />
                                                    <div class="text-left">
                                                        <div class="font-semibold text-lg">{{ getTopicDisplayName(topic) }}</div>
                                                        <div v-if="shouldShowTopicId(topic)" class="text-sm text-muted-foreground mt-1">
                                                            {{ getTopicId(topic) }}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="flex items-center gap-3">
                                                    <Badge v-if="filters[getTopicName(topic)]?.trim()" variant="default" class="px-3 py-1">
                                                        <CheckCircle class="h-3 w-3 mr-1" />
                                                        Filtered
                                                    </Badge>
                                                    <Badge v-else variant="secondary" class="px-3 py-1"> No Filter </Badge>
                                                    <Button
                                                        v-if="filters[getTopicName(topic)]?.trim()"
                                                        variant="ghost"
                                                        size="sm"
                                                        @click.stop="clearTopicFilter(getTopicName(topic))"
                                                        class="h-9 w-9 p-0 text-destructive hover:text-destructive hover:bg-destructive/15 transition-colors">
                                                        <X class="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardHeader>
                                    </CollapsibleTrigger>

                                    <CollapsibleContent>
                                        <CardContent
                                            class="pt-0 pb-4 px-6"
                                            :id="`topic-content-${getTopicId(topic)}`"
                                            role="region"
                                            :aria-label="`Filter configuration for ${getTopicDisplayName(topic)}`">
                                            <div class="space-y-4">
                                                <div class="space-y-3">
                                                    <Alert class="mb-4 border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/50">
                                                        <InfoIcon class="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                                        <AlertTitle>{{ syntaxGuide }}</AlertTitle>
                                                        <AlertDescription class="mt-2">
                                                            <p class="text-sm mb-3">
                                                                Define conditions to include only the data you want from
                                                                <strong>{{ getTopicDisplayName(topic) }}</strong
                                                                >.
                                                            </p>
                                                            <div class="mb-3">
                                                                <p class="text-sm font-medium">{{ syntaxDescription }}</p>
                                                            </div>
                                                            <div class="space-y-2">
                                                                <code
                                                                    v-for="example in exampleFilters"
                                                                    :key="example"
                                                                    class="bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 px-2 py-1 rounded text-xs font-mono block">
                                                                    {{ example }}
                                                                </code>
                                                            </div>
                                                        </AlertDescription>
                                                    </Alert>
                                                </div>

                                                <div
                                                    class="border rounded-lg overflow-hidden bg-background shadow-sm focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-colors">
                                                    <div class="flex items-center justify-between px-3 py-2 bg-muted/50 border-b text-sm">
                                                        <span class="font-medium text-muted-foreground">{{ editorTitle }}</span>
                                                        <div class="flex gap-2">
                                                            <span class="text-xs text-muted-foreground">
                                                                {{ filters[getTopicName(topic)]?.length || 0 }} chars
                                                            </span>
                                                            <Button
                                                                v-if="filters[getTopicName(topic)]?.trim()"
                                                                variant="ghost"
                                                                size="sm"
                                                                @click="clearTopicFilter(getTopicName(topic))"
                                                                class="h-6 px-2 text-xs hover:bg-destructive/15 hover:text-destructive">
                                                                Clear
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <VueMonacoEditor
                                                        :value="filters[getTopicName(topic)] || ''"
                                                        @change="(value) => updateFilter(getTopicName(topic), value)"
                                                        :theme="monacoTheme"
                                                        :language="editorLanguage"
                                                        :height="editorHeightNumber"
                                                        :options="enhancedEditorOptions"
                                                        class="w-full"
                                                        :style="{ minHeight: editorHeight, height: editorHeight }"
                                                        :aria-label="`${language} filter for ${getTopicDisplayName(topic)}`" />
                                                </div>
                                            </div>
                                        </CardContent>
                                    </CollapsibleContent>
                                </Collapsible>
                            </Card>
                        </div>
                    </div>
                </ScrollArea>
            </div>
        </div>

        <!-- Footer -->
        <template #footer>
            <div class="flex gap-3 justify-between w-full">
                <div class="flex items-center gap-2 text-sm text-muted-foreground">
                    <div class="flex items-center gap-1">
                        <div class="h-2 w-2 bg-primary rounded-full"></div>
                        <span>{{ topicsWithFilters.length }} filtered</span>
                    </div>
                    <span>•</span>
                    <div class="flex items-center gap-1">
                        <div class="h-2 w-2 bg-gray-400 rounded-full"></div>
                        <span>{{ selectedTopics.length - topicsWithFilters.length }} unfiltered</span>
                    </div>
                </div>

                <div class="flex gap-3">
                    <Button variant="outline" @click="goBack">
                        <ArrowLeft class="h-4 w-4 mr-2" />
                        Back
                    </Button>
                    <Button variant="outline" @click="closeDialog"> Cancel </Button>
                    <Button
                        @click="executeSnapshot"
                        :disabled="!selectedTopics.length || isExecuting"
                        :class="{ 'cursor-not-allowed': !selectedTopics.length || isExecuting }">
                        <Loader2 v-if="isExecuting" class="mr-2 h-4 w-4 animate-spin" />
                        <span v-if="!isExecuting">Snapshot</span>
                        <span v-else>Executing...</span>
                    </Button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { ChevronRight, X, Loader2, ArrowLeft, RotateCcw, CheckCircle, Info as InfoIcon } from 'lucide-vue-next';
import { VueMonacoEditor } from '@guolao/vue-monaco-editor';
import { useMonacoTheme } from '@/composables/useMonacoTheme';

import Modal from '@/components/global/modals/Modal.vue';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const props = defineProps({
    open: {
        type: Boolean,
        default: false
    },
    selectedTopics: {
        type: Array,
        default: () => []
    },
    sourceId: {
        type: String,
        required: true
    },
    connector: {
        type: String,
        required: true
    },
    language: {
        type: String,
        default: 'SQL'
    }
});

const emit = defineEmits(['update:open', 'execute', 'back']);

const isOpen = computed({
    get: () => props.open,
    set: (value) => emit('update:open', value)
});

// Monaco editor configuration
const { monacoTheme } = useMonacoTheme();

// Enhanced editor height configuration
const editorHeightNumber = computed(() => {
    // Fixed height for consistency and better user experience
    return 200; // Increased from variable height for better usability
});

const editorHeight = computed(() => `${editorHeightNumber.value}px`);

const editorOptions = {
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    fontSize: 14,
    lineHeight: 20,
    lineNumbers: 'on',
    wordWrap: 'on',
    automaticLayout: true,
    // Use Monaco's default SQL language features
    bracketMatching: 'always',
    autoIndent: 'full',
    formatOnType: true,
    formatOnPaste: true
};

// Enhanced options for better UX
const enhancedEditorOptions = computed(() => ({
    ...editorOptions,
    placeholder: editorPlaceholder.value,
    suggestOnTriggerCharacters: true,
    quickSuggestions: true,
    parameterHints: { enabled: true },
    contextmenu: true,
    selectOnLineNumbers: true,
    roundedSelection: false,
    readOnly: false,
    cursorStyle: 'line',
    folding: false,
    renderLineHighlight: 'line',
    occurrencesHighlight: true
}));

// State
const filters = ref({});
const expandedTopics = ref({});
const allExpanded = ref(false);
const isExecuting = ref(false);

// Computed
const topicsWithFilters = computed(() => props.selectedTopics.filter((topic) => filters.value[getTopicName(topic)]?.trim()));

// Language-specific configuration
const syntaxGuide = computed(() => {
    return 'Filter Syntax Guide';
    // switch (props.language.toUpperCase()) {
    //     case 'SQL':
    //         return 'Filter Syntax Guide';
    //     case 'JSON':
    //         return 'Filter Syntax Guide';
    //     default:
    //         return 'Filter Syntax Guide';
    // }
});

const syntaxDescription = computed(() => {
    switch (props.language.toUpperCase()) {
        case 'SQL':
            return 'Use SQL-style expressions';
        case 'JSON':
            return 'Use JSON filter syntax:';
        default:
            return `Use ${props.language} filter syntax`;
    }
});

const exampleFilters = computed(() => {
    switch (props.language.toUpperCase()) {
        case 'SQL':
            return ["created_date >= '2024-01-01' AND status = 'active'", "amount > 100 OR priority IN ('high', 'urgent')"];
        case 'JSON':
            return ['{ "status": "active" }', '{ "priority": { "$in": ["high", "urgent"] }, "amount": { "$gt": 100 } }'];
        default:
            return ['// Language-specific examples not available', '// Please refer to your connector documentation'];
    }
});

const editorTitle = computed(() => {
    switch (props.language.toUpperCase()) {
        case 'SQL':
            return 'SQL Editor';
        case 'JSON':
            return 'JSON Editor';
        default:
            return `${props.language} Editor`;
    }
});

const editorLanguage = computed(() => {
    return props.language.toLowerCase();
});

const editorPlaceholder = computed(() => {
    switch (props.language.toUpperCase()) {
        case 'SQL':
            return "Enter SQL WHERE conditions (e.g., status = 'active' AND created_date >= '2024-01-01')";
        case 'JSON':
            return 'Enter JSON filter conditions (e.g., { "status": "active" })';
        default:
            return `Enter ${props.language} filter conditions`;
    }
});

const generatePreviewQuery = (topicName, filter) => {
    if (!filter?.trim()) return '';

    switch (props.language.toUpperCase()) {
        case 'SQL':
            return `SELECT * FROM ${topicName}\nWHERE ${filter}\nLIMIT 1000;`;
        case 'JSON':
            return `db.${topicName}.find(${filter})`;
        default:
            return `Filter: ${filter}`;
    }
};

// Helper functions
const getTopicName = (topic) => {
    return topic?.name || topic;
};

const getTopicId = (topic) => {
    return topic?.id || topic?.name || topic;
};

// New helper for proper topic display
const getTopicDisplayName = (topic) => {
    // If topic has both name and id, show the name
    // If topic only has name or is a string, show that
    return topic?.name || topic;
};

const shouldShowTopicId = (topic) => {
    // Only show ID as subtitle if:
    // 1. Topic has both name and id properties
    // 2. The id is different from the name
    return topic?.id && topic?.name && topic.id !== topic.name;
};

const updateFilter = (topicName, value) => {
    filters.value[topicName] = value;
};

const clearTopicFilter = (topicName) => {
    filters.value[topicName] = '';
};

const clearAllFilters = () => {
    Object.keys(filters.value).forEach((key) => {
        filters.value[key] = '';
    });
};

const toggleTopicExpanded = (topicId) => {
    expandedTopics.value[topicId] = !expandedTopics.value[topicId];
};

const toggleAllExpanded = () => {
    allExpanded.value = !allExpanded.value;
    props.selectedTopics.forEach((topic) => {
        expandedTopics.value[getTopicId(topic)] = allExpanded.value;
    });
};

const closeDialog = () => {
    isOpen.value = false;
};

const goBack = () => {
    emit('back');
    closeDialog();
};

const executeSnapshot = async () => {
    isExecuting.value = true;

    try {
        // Build additional conditions for topics that have filters
        const additionalConditions = [];

        topicsWithFilters.value.forEach((topic) => {
            const topicName = getTopicName(topic);
            const filter = filters.value[topicName]?.trim();

            if (filter) {
                additionalConditions.push({
                    data_collection: topicName,
                    filter: filter
                });
            }
        });

        // Create snapshot body with additional conditions
        const snapshotBody = {
            source_id: props.sourceId,
            connector: props.connector,
            topic_names: props.selectedTopics.map((topic) => getTopicName(topic))
        };

        // Only add additional_conditions if there are any
        if (additionalConditions.length > 0) {
            snapshotBody.additional_conditions = additionalConditions;
        }

        emit('execute', snapshotBody);
        // Let parent component handle closing the dialog on success
    } catch (error) {
        console.error('Failed to execute filtered snapshot:', error);
        // Reset execution state only on error
        isExecuting.value = false;
    }
};

// Initialize expanded state when topics change
watch(
    () => props.selectedTopics,
    (newTopics) => {
        // Initialize expanded state for new topics
        newTopics.forEach((topic) => {
            const topicId = getTopicId(topic);
            if (!(topicId in expandedTopics.value)) {
                expandedTopics.value[topicId] = false;
            }
        });

        // Initialize filters for new topics
        newTopics.forEach((topic) => {
            const topicName = getTopicName(topic);
            if (!(topicName in filters.value)) {
                filters.value[topicName] = '';
            }
        });
    },
    { immediate: true }
);

// Simple modal state watcher
watch(isOpen, (newValue) => {
    if (!newValue) {
        // Reset state when dialog closes
        isExecuting.value = false;
        allExpanded.value = false;
    }
});
</script>
